<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  commission: { type: Object, default: null },
});

const emit = defineEmits(['details']);
const { t } = useI18n();
const detail = computed(() => props.commission || {});
const money = value =>
  value?.amount ?? t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY');
const paymentMethod = computed(
  () =>
    detail.value.liquida ||
    detail.value.forma_pagamento ||
    t('REAL_ESTATE_FINANCE.TABLE.NOT_INFORMED')
);
const installmentCount = computed(() => detail.value.parcelas?.length || 0);
</script>

<template>
  <section
    data-testid="finance-installment-commission-summary"
    class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
  >
    <header class="flex items-center gap-2">
      <span
        class="i-lucide-circle-dollar-sign size-5 text-lais-primary"
        aria-hidden="true"
      />
      <h2 class="font-semibold text-lais-text">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.COMMISSION_TITLE') }}
      </h2>
    </header>
    <dl class="mt-4 grid gap-4 sm:grid-cols-2">
      <div>
        <dt class="text-sm text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.DETAIL.COMMISSION_TOTAL') }}
        </dt>
        <dd class="mt-1 font-medium text-lais-text">
          {{ money(detail.valor_comissao_total) }}
        </dd>
      </div>
      <div>
        <dt class="text-sm text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.DETAIL.COMMISSION_PAYMENT_METHOD') }}
        </dt>
        <dd class="mt-1 font-medium text-lais-text">{{ paymentMethod }}</dd>
      </div>
      <div>
        <dt class="text-sm text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.DETAIL.COMMISSION_DEDUCTION') }}
        </dt>
        <dd class="mt-1 font-medium text-lais-text">
          {{ money(detail.deducao) }}
        </dd>
      </div>
      <div>
        <dt class="text-sm text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.DETAIL.COMMISSION_TYPE') }}
        </dt>
        <dd class="mt-1 flex flex-wrap gap-2">
          <span
            v-if="detail.tipo_pagamento"
            class="rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700"
          >
            {{ detail.tipo_pagamento }}
          </span>
          <span
            v-if="installmentCount > 1"
            class="rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700"
          >
            {{
              t('REAL_ESTATE_FINANCE.DETAIL.COMMISSION_INSTALLMENT_COUNT', {
                count: installmentCount,
              })
            }}
          </span>
          <span v-if="!detail.tipo_pagamento && !installmentCount">
            {{ t('REAL_ESTATE_FINANCE.TABLE.NOT_INFORMED') }}
          </span>
        </dd>
      </div>
      <div>
        <dt class="text-sm text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.DETAIL.NET') }}
        </dt>
        <dd class="mt-1 font-semibold text-lais-primary">
          {{ money(detail.valor_comissao_liquida) }}
        </dd>
      </div>
    </dl>
    <footer class="mt-4 border-t border-lais-border-soft pt-3">
      <LaisPrimaryButton size="sm" variant="ghost" @click="emit('details')">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.COMMISSION_DETAILS') }}
      </LaisPrimaryButton>
    </footer>
  </section>
</template>
